import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, GOOGLE_CLIENT_ID } from '../config';

const TURNSTILE_SITE_KEY = '0x4AAAAAADLfFn8frPa_Pr7i';

function SignInPage({ onBack, onSignup, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const turnstileRef = useRef(null);

  const handleGoogleSignIn = async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v3/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Google sign-in failed');
      } else {
        localStorage.setItem('astrogem_token', data.token);
        localStorage.setItem('astrogem_api_key', data.api_key);
        localStorage.setItem('astrogem_user', JSON.stringify(data.user));
        onSuccess(data);
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Turnstile
    if (!document.querySelector('script[src*="challenges.cloudflare.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Google Sign-In script
    if (GOOGLE_CLIENT_ID && !document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    const checkAndRender = () => {
      if (window.turnstile && turnstileRef.current) {
        try {
          window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token) => setTurnstileToken(token)
          });
        } catch (e) {}
      } else {
        setTimeout(checkAndRender, 200);
      }

      // Render Google button
      if (window.google?.accounts?.id && GOOGLE_CLIENT_ID) {
        const googleBtn = document.getElementById('google-signin-btn');
        if (googleBtn && !googleBtn.dataset.rendered) {
          googleBtn.dataset.rendered = 'true';
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleSignIn
          });
          window.google.accounts.id.renderButton(googleBtn, {
            theme: 'filled_black', size: 'large', text: 'signin_with', shape: 'rectangular', width: 320
          });
        }
      }
    };
    checkAndRender();
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) return setError('Email and password required');
    if (!turnstileToken) return setError('Please complete the security check');

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v3/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, turnstileToken })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Login failed');
        if (window.turnstile) window.turnstile.reset();
        setTurnstileToken('');
      } else {
        localStorage.setItem('astrogem_token', data.token);
        localStorage.setItem('astrogem_api_key', data.api_key);
        localStorage.setItem('astrogem_user', JSON.stringify(data.user));
        onSuccess(data);
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6">← Back</button>

        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🪔</div>
            <h1 className="text-3xl font-bold mb-2">Sign In</h1>
            <p className="text-white/60 text-sm">Welcome back to ASTROGEM API</p>
          </div>

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="flex justify-center mb-4">
                <div id="google-signin-btn"></div>
              </div>
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-slate-700"></div>
                <span className="px-3 text-xs text-white/50">or continue with email</span>
                <div className="flex-1 border-t border-slate-700"></div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            <div className="flex justify-center py-2">
              <div ref={turnstileRef}></div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm text-red-300">
                ⚠️ {error}
              </div>
            )}

            <button type="submit" disabled={loading || !turnstileToken}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-bold transition">
              {loading ? '⏳ Signing in...' : '🪔 Sign In'}
            </button>

            <p className="text-center text-sm text-white/60">
              No account?{' '}
              <button type="button" onClick={onSignup} className="text-purple-300 hover:text-purple-200 font-semibold">
                Sign Up Free
              </button>
            </p>
          </form>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">🛡️ Protected by Cloudflare Turnstile</p>
      </div>
    </div>
  );
}

export default SignInPage;
